import { useEffect, useState } from "react";
import { useTranslate } from "react-admin";

import addMinutes from "date-fns/addMinutes";

import { Button } from "../MuiGenerals";
import { RESCAN_LOCK_TIME_MS } from "../../utils/constant";
import { toDisplayTime } from "../../utils/helper";
import { useZusDialogStore } from "../../zustand/store";

import InfoBanner from "./InfoBanner";
import RiskLevelBadge from "./RiskLevelBadge";

interface RescanLockedProps {
  onProceed: () => void;
}

// Format the remaining cooldown as "M:SS" or "SSs" so the user can see at a
// glance how long they have to wait. Tick once per second so the message
// stays in sync with `Date.now()` — the lock-step body uses the same value
// to compute the "next available at" timestamp.
const formatRemaining = (ms: number): string => {
  if (ms <= 0) return "0:00";
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

// First step in the rescan flow when the wallet is currently within the
// 5-minute cooldown after a fresh rescan. Informational only — the Rescan
// button is always enabled and proceeds directly to the confirm step. The
// backend's own lock check (`-20` "called within the lock window") is the
// real enforcement; if a user proceeds before the cooldown is over, the
// dialog shows a localized toast and stays on the confirm step rather
// than failing silently.
const RescanLocked = ({ onProceed }: RescanLockedProps) => {
  const translate = useTranslate();
  const t = (key: string, options?: Record<string, any>) =>
    options
      ? translate(`walletManagement.${key}`, options)
      : translate(`walletManagement.${key}`);
  const zusDialog = useZusDialogStore();
  const handleClose = () => zusDialog.close();

  const {
    display_name,
    wallet_address,
    current_risk_level,
    last_rescan_time,
    rescan_time_raw,
    triggered_by,
    lock_time_ms,
  } = zusDialog.meta || {};

  // Tick once a second so the countdown stays in sync with `Date.now()`.
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Prefer the API-provided lock duration, fall back to the compile-time
  // constant when the API hasn't responded yet. Used both to compute the
  // remaining-time countdown and the absolute "next available at" timestamp.
  const lockDuration = (lock_time_ms as number | undefined) ??
    RESCAN_LOCK_TIME_MS;

  const lockEndTime = rescan_time_raw
    ? new Date(rescan_time_raw).getTime() + lockDuration
    : 0;
  const remainingMs = Math.max(0, lockEndTime - now);
  const isLocked = remainingMs > 0;
  const countDown = formatRemaining(remainingMs);

  // Absolute "next available at" — kept so users can compare against the
  // current clock even after the cooldown has expired.
  const nextRescanTime = rescan_time_raw
    ? toDisplayTime(
        addMinutes(new Date(rescan_time_raw), lockDuration / (60 * 1000)),
      )
    : "";

  const reminderText = t("rescan_locked_reminder", {
    rescanTime: last_rescan_time || "",
    rescannedBy: triggered_by || "",
    countDown,
  });

  return (
    <>
      <div style={{ padding: "12px 24px 12px" }}>
        <div style={{ marginBottom: "12px" }}>
          <InfoBanner tone="info">{reminderText}</InfoBanner>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>{t("display_name")}</div>
          <div style={valueStyle}>{display_name}</div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>{t("walletAddress")}</div>
          <div style={valueStyle}>{wallet_address}</div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>{t("current_risk_level")}</div>
          <div style={{ ...valueStyle, display: "flex", alignItems: "center" }}>
            <RiskLevelBadge riskLevel={current_risk_level} />
          </div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>{t("last_rescan")}</div>
          <div style={valueStyle}>{last_rescan_time || t("never")}</div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          padding: "8px 24px 20px",
        }}
      >
        {/* Disable Rescan while the cooldown is still active; the user has
            to wait for the lock window to elapse. The countdown in the
            banner above tells them exactly how long. If the backend rejects
            anyway after the timer expires, the `-20` handler still fires
            — defense in depth, not a substitute for client-side gating. */}
        <Button
          color="secondary"
          variant="contained"
          onClick={onProceed}
          disabled={isLocked}
        >
          {t("rescan")}
        </Button>
        <Button color="error" variant="contained" onClick={handleClose}>
          {translate("common.cancel")}
        </Button>
      </div>
    </>
  );
};

const rowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "140px 1fr",
  gap: "12px",
  padding: "6px 0",
  alignItems: "center",
};

const labelStyle: React.CSSProperties = {
  color: "#8C8C8C",
  fontSize: "13px",
};

const valueStyle: React.CSSProperties = {
  color: "#262626",
  fontSize: "13px",
  wordBreak: "break-all",
};

export default RescanLocked;